import { queryOptions } from "@tanstack/react-query";
import { listNavItems } from "./nav.functions";

export type NavItem = {
  id: string;
  label: string;
  parent_id: string | null;
  template_slug: string | null;
  external_url: string | null;
  display_order: number;
  is_active: boolean;
  open_in_new_tab: boolean;
  created_at: string;
  updated_at: string;
};

/**
 * The database scales to zero after idle; the first API calls while it resumes
 * can take 15s+ or fail outright. The nav must never be missing that long, so:
 * - the last successful menu is cached in localStorage and used as
 *   placeholderData — navigation renders instantly on refresh, then revalidates
 * - retries continue with capped backoff long enough to outlast a DB resume
 */
const NAV_CACHE_KEY = "mii-nav-cache-v1";

function readNavCache(): NavItem[] | undefined {
  if (typeof localStorage === "undefined") return undefined;
  try {
    const raw = localStorage.getItem(NAV_CACHE_KEY);
    return raw ? (JSON.parse(raw) as NavItem[]) : undefined;
  } catch {
    return undefined;
  }
}

export const navItemsQueryOptions = () =>
  queryOptions({
    queryKey: ["nav_items"],
    queryFn: async () => {
      const data = (await listNavItems()) as NavItem[];
      try {
        localStorage.setItem(NAV_CACHE_KEY, JSON.stringify(data));
      } catch {
        // storage full/blocked — cache is best-effort
      }
      return data;
    },
    placeholderData: readNavCache(),
    staleTime: 5 * 60_000,
    retry: 8,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });

export function navHref(item: NavItem): string {
  if (item.external_url) return item.external_url;
  if (item.template_slug) return `/p/${item.template_slug}`;
  return "#";
}

export type NavTree = {
  roots: NavItem[];
  children: Map<string, NavItem[]>;
};

/**
 * Split a flat nav list into one level of nesting. Children whose parent is not
 * present as a root (deleted or filtered out, e.g. an inactive parent) are dropped.
 */
export function buildNavTree(items: NavItem[]): NavTree {
  const sorted = [...items].sort((a, b) => a.display_order - b.display_order);
  const roots = sorted.filter((n) => !n.parent_id);
  const rootIds = new Set(roots.map((n) => n.id));
  const children = new Map<string, NavItem[]>();
  for (const n of sorted) {
    if (!n.parent_id || !rootIds.has(n.parent_id)) continue;
    const bucket = children.get(n.parent_id);
    if (bucket) bucket.push(n);
    else children.set(n.parent_id, [n]);
  }
  return { roots, children };
}
